import { useEffect, useMemo, useState } from 'react';
import { ArrowUpRight, Layers, Library, Map as MapIcon, Search } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Sidebar } from '@/components/layout/Sidebar';
import { useAuth } from '@/lib/auth';
import {
  FALLBACK_PROJECTS,
  PROJECT_INDEX_URL,
  formatDate,
  groupProjects,
  imageUrl,
  mapUrl,
  matchesQuery,
  parseProjectIndex,
} from '@/lib/mapProjects';
import type { MapProject } from '@/lib/mapProjects';

/**
 * Kaarten & Bibliotheek (menu item, id 'viewers'): the team's map catalog.
 *   Kaarten     GeoLibre project catalog: /map/data/project/index.json (contract in lib/mapProjects.ts),
 *               one compact card per map with its preview image, in sections (basiskaart, themakaarten,
 *               projectkaarten), searchable; a card opens /map/?url=<origin>/map/data/project/<file> in a new tab.
 *               Never /maps here: the /map Traefik router's PathPrefix swallows that prefix.
 *   Meer        Bibliotheek (sb-api OGC API collections, Portolan open data as "extern") and the thematic
 *               viewers (/viewer, sb-viewer SvelteKit, to be superseded), as a compact row below the maps.
 */

const palette = {
  viewer: '#5B8DEF',
  viewerBorder: '#BFD3F7',
  viewerBg: '#F5F8FF',
  placeholder: '#EEF3FB',
};

const CATALOG_URL = 'https://api.studiobereikbaar.nl/oapi/collections?f=html';
const OPEN_DATA_URL = 'https://data.studiobereikbaar.nl/';
const HIDDEN_TAGS = new Set(['themakaart', 'voorbeeld', 'roland']); // shown as a badge or credit, not as a tag

interface Entry {
  id: string;
  title: string;
  description: string;
  href: string;
  icon: LucideIcon;
  newTab?: boolean;
  secondary?: { label: string; href: string };
}

const entries: Entry[] = [
  {
    id: 'bibliotheek',
    title: 'Bibliotheek',
    description: 'De datacatalogus: de lagen in de database (OGC API), met bron en licentie, te openen in QGIS.',
    href: CATALOG_URL,
    icon: Library,
    newTab: true,
    secondary: { label: 'Open data (extern)', href: OPEN_DATA_URL },
  },
  {
    id: 'viewers',
    title: 'Thematische viewers',
    description: 'Thematische kaarten op nationale schaal, demo’s en projectgalerijen, met print naar PDF.',
    href: '/viewer',
    icon: Layers,
  },
];

type ProjectState = { projects: MapProject[]; source: 'index' | 'fallback' };

/** Project list from index.json; the hard-coded pair whenever it is missing, redirected or empty. */
function useMapProjects(): ProjectState {
  const [state, setState] = useState<ProjectState>({ projects: FALLBACK_PROJECTS, source: 'fallback' });

  useEffect(() => {
    const ctrl = new AbortController();
    // redirect: 'error' = an expired gate session (302 to /login) counts as "no index", not as HTML.
    fetch(PROJECT_INDEX_URL, { signal: ctrl.signal, redirect: 'error', headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data: unknown) => {
        const projects = parseProjectIndex(data);
        if (projects.length > 0) setState({ projects, source: 'index' });
      })
      .catch(() => {
        /* keep the fallback */
      });
    return () => ctrl.abort();
  }, []);

  return state;
}

function MapCard({ project: p, origin }: { project: MapProject; origin: string }) {
  const [broken, setBroken] = useState(false);
  const preview = p.tags?.includes('voorbeeld');
  const credit = p.tags?.includes('roland');
  const tags = (p.tags ?? []).filter((t) => !HIDDEN_TAGS.has(t));

  return (
    <li>
      <a
        href={mapUrl(p.file, origin)}
        target="_blank"
        rel="noopener noreferrer"
        className="group relative flex h-full flex-col overflow-hidden rounded-xl border bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
        style={{ borderColor: palette.viewerBorder, outlineColor: palette.viewer }}
      >
        <div className="relative aspect-[16/9] overflow-hidden" style={{ backgroundColor: palette.placeholder }}>
          {p.image && !broken ? (
            <img
              src={imageUrl(p.image)}
              alt=""
              loading="lazy"
              onError={() => setBroken(true)}
              className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <MapIcon size={36} style={{ color: palette.viewerBorder }} aria-hidden="true" />
            </div>
          )}
          {preview && (
            <span className="absolute left-2 top-2 rounded-full bg-white/90 px-2 py-0.5 text-[11px] font-medium text-gray-600 shadow-sm">
              Voorbeeldweergave
            </span>
          )}
          <span className="absolute right-2 top-2 rounded-full bg-white/90 p-1 opacity-0 shadow-sm transition group-hover:opacity-100">
            <ArrowUpRight size={14} style={{ color: palette.viewer }} aria-hidden="true" />
          </span>
        </div>
        <div className="flex flex-1 flex-col gap-1 p-3">
          <h3 className="text-sm font-semibold leading-snug text-sb-black group-hover:underline">
            {p.name}
            <span className="sr-only"> (opent in een nieuw tabblad)</span>
          </h3>
          {p.description && (
            <p className="line-clamp-2 text-xs leading-relaxed text-sb-dark-grey" title={p.description}>
              {p.description}
            </p>
          )}
          <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-2 text-[11px] text-gray-500">
            {p.layers && <span>{p.layers === 1 ? '1 laag' : `${p.layers} lagen`}</span>}
            {p.layers && p.updated && <span aria-hidden="true">·</span>}
            {p.updated && <span>{formatDate(p.updated)}</span>}
            {credit && (
              <span className="rounded-full px-2 py-0.5 font-medium" style={{ backgroundColor: palette.viewerBg, color: palette.viewer }}>
                Roland Kager
              </span>
            )}
            {tags.map((t) => (
              <span key={t} className="rounded-full px-2 py-0.5" style={{ backgroundColor: palette.viewerBg }}>
                {t}
              </span>
            ))}
          </div>
        </div>
      </a>
    </li>
  );
}

export default function MapsLibrary() {
  const { user } = useAuth();
  const { projects, source } = useMapProjects();
  const [query, setQuery] = useState('');
  const origin = window.location.origin;
  const groups = useMemo(() => groupProjects(projects.filter((p) => matchesQuery(p, query))), [projects, query]);
  const shown = groups.reduce((n, g) => n + g.projects.length, 0);

  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: palette.viewerBg }}>
      <Sidebar activeItem="viewers" user={user} />

      {/* relative: the sr-only labels are position:absolute; without a positioned ancestor inside the scroller
          they sit on the page itself and stretch the window past the screen (15-10: the whole window scrolled). */}
      <main id="main-content" className="relative flex-1 overflow-y-auto" data-source={source}>
        <div className="mx-auto flex max-w-7xl flex-col gap-8 px-6 py-8 md:px-10">
          <header className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-sb-black">Kaarten</h1>
              <p className="mt-1 text-sm text-sb-grey">
                {projects.length} kaarten in GeoLibre. Een kaart opent in een nieuw tabblad, met de lagen en de stijl van
                die kaart.
              </p>
            </div>
            <label className="relative w-full sm:w-72">
              <span className="sr-only">Zoek een kaart</span>
              <Search
                size={16}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Zoek op naam, thema of tag"
                className="w-full rounded-lg border bg-white py-2 pl-9 pr-3 text-sm text-sb-black shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2"
                style={{ borderColor: palette.viewerBorder }}
              />
            </label>
          </header>

          {groups.map((g) => (
            <section key={g.id} aria-labelledby={`kaarten-${g.id}`}>
              <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h2 id={`kaarten-${g.id}`} className="text-lg font-semibold text-sb-black">
                  {g.title}
                </h2>
                <span className="text-xs font-medium" style={{ color: palette.viewer }}>
                  {g.projects.length}
                </span>
                {g.hint && <p className="text-xs text-gray-500">{g.hint}</p>}
              </div>
              <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                {g.projects.map((p) => (
                  <MapCard key={p.id} project={p} origin={origin} />
                ))}
              </ul>
            </section>
          ))}

          {shown === 0 && (
            <p className="rounded-xl border bg-white p-6 text-sm text-sb-dark-grey" style={{ borderColor: palette.viewerBorder }}>
              Geen kaart gevonden voor “{query}”.
            </p>
          )}

          <section aria-labelledby="kaarten-meer" className="border-t pt-6" style={{ borderColor: palette.viewerBorder }}>
            <h2 id="kaarten-meer" className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Meer kaarten en data
            </h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {entries.map((e) => {
                const Icon = e.icon;
                return (
                  <div
                    key={e.id}
                    className="relative flex items-start gap-3 rounded-xl border bg-white p-4 shadow-sm"
                    style={{ borderColor: palette.viewerBorder }}
                  >
                    <Icon size={22} className="mt-0.5 shrink-0" style={{ color: palette.viewer }} aria-hidden="true" />
                    <div className="min-w-0">
                      <a
                        href={e.href}
                        target={e.newTab ? '_blank' : undefined}
                        rel={e.newTab ? 'noopener noreferrer' : undefined}
                        className="inline-flex items-center gap-1 text-sm font-semibold hover:underline"
                        style={{ color: palette.viewer }}
                      >
                        {e.title}
                        <ArrowUpRight size={14} aria-hidden="true" />
                        {e.newTab && <span className="sr-only">(opent in een nieuw tabblad)</span>}
                      </a>
                      <p className="mt-0.5 text-xs text-sb-dark-grey">{e.description}</p>
                      {e.secondary && (
                        <a
                          href={e.secondary.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-1 inline-block text-xs text-gray-500 underline hover:text-gray-700"
                        >
                          {e.secondary.label}
                          <span className="sr-only">(opent in een nieuw tabblad)</span>
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-4 text-xs text-gray-500">GeoLibre-demo; de eigen build volgt.</p>
          </section>
        </div>
      </main>
    </div>
  );
}
